import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {type ChangeEvent, useState} from "react";
import {useTranslation} from "react-i18next";

import type {ValueOrderType} from "@reva/shared/value-order";

import {useRevaDispatch} from "../redux/revaHooks";
import type {ApiQueryError} from "../redux/types";
import {attachValueOrder} from "../redux/valueOrdersThunk";
import {buildApiQueryError, generateDefaultQueryError} from "../util/errorUtil";
import I18nErrorMessage from "./I18nErrorMessage";

interface AttachInspectionOrderDialogProps {
    /** state if the dialog should be visible */
    visible: boolean;
    /** order type to attach */
    orderType?: ValueOrderType;
    /** callback to execute when the dialog gets closed */
    onClose: () => void;
}

/**
 * Dialog to attach an Inspection Value Order to the REVA valuation by a given external order number.
 */
export function AttachInspectionOrderDialog({
    visible,
    orderType = "Inspection",
    onClose,
}: AttachInspectionOrderDialogProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const [externalOrderNumber, setExternalOrderNumber] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [attachError, setAttachError] = useState<ApiQueryError | null>(null);

    const handleExternalOrderNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (inProgress) return;
        setExternalOrderNumber(e.target.value);
    };

    const handleCancel = () => {
        if (inProgress) return;

        setExternalOrderNumber("");
        setAttachError(null);
        onClose();
    };

    const handleAttach = async () => {
        if (inProgress || !externalOrderNumber) return;

        setInProgress(true);
        setAttachError(null);

        try {
            await dispatch(attachValueOrder(externalOrderNumber, orderType));
        } catch (e) {
            setInProgress(false);
            setAttachError(buildApiQueryError(generateDefaultQueryError(e as Error)));
            return;
        }

        setInProgress(false);
        onClose();
    };

    const footer = (
        <div>
            <Button
                className="p-button-text"
                label={t("general.cancel")}
                icon="pi pi-times"
                disabled={inProgress}
                onClick={handleCancel}
            />
            <Button
                className="ml-2"
                label={t("attachOrderDialog.attach")}
                icon="pi pi-check"
                autoFocus
                disabled={inProgress || !externalOrderNumber}
                loading={inProgress}
                onClick={() => void handleAttach()}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={handleCancel}
            footer={footer}
            header={t("attachOrderDialog.title", {orderType: t(`order.orderType.${orderType}`)})}
            showCloseIcon={false}
            style={{maxWidth: "50rem"}}
        >
            <div>{t("attachOrderDialog.text", {orderType: t(`order.orderType.${orderType}`)})}</div>
            <div className="rounded-border bg-surface-100 dark:bg-surface-700 p-4 panelForInputs mt-4">
                <div className="field">
                    <label htmlFor="attachDialogInputExternalOrderNumber">
                        {t("attachOrderDialog.externalOrderNumber")}
                    </label>
                    <div className="w-full">
                        <InputText
                            id="attachDialogInputExternalOrderNumber"
                            className="w-full"
                            value={externalOrderNumber}
                            onChange={handleExternalOrderNumberChange}
                            disabled={inProgress}
                            placeholder={`HS-${new Date().toISOString().substring(2, 4)}-XXXX-XXXXXX`}
                        />
                    </div>
                </div>
            </div>
            <I18nErrorMessage titleI18nKey="attachOrderDialog.error" errorObject={attachError} className="mt-8" />
        </Dialog>
    );
}
